import { setStoredPreferredLanguage } from "shared-core/services/i18n.ts";
import { createLocaleCatalog } from "shared-core/services/i18n-catalog.ts";
import type {
  Locale,
  LocaleNamespace,
  LocaleNamespaceLoader,
} from "shared-core/services/i18n-types.ts";
import type { OrganizationType } from "shared-core/types/organization.ts";
import { getOrganizationTranslationValues } from "shared-core/utils/organization-translation.ts";
import { createI18n } from "vue-i18n";

const FALLBACK: Locale = "en";

export type I18nRuntime = ReturnType<typeof createI18nRuntime>;

interface I18nRuntimeOptions {
  getOrganizationType?: () => OrganizationType | null | undefined;
}

// The returned `i18n` is vue-i18n's deeply-generic `I18n<...>` instantiated from
// the literal options below; that type is intractable to spell out by hand, and
// the public `I18nRuntime` alias is `ReturnType<typeof createI18nRuntime>` (so an
// explicit return type here would be circular). Inference is correct as-is.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createI18nRuntime(
  // eslint-disable-next-line @typescript-eslint/require-await
  loadNamespace: LocaleNamespaceLoader = async () => ({}),
  options: I18nRuntimeOptions = {},
) {
  const i18n = createI18n({
    legacy: false,
    locale: FALLBACK,
    fallbackLocale: FALLBACK,
    messages: {},
  });
  const catalog = createLocaleCatalog<Locale, LocaleNamespace>(
    loadNamespace,
    FALLBACK,
  );
  const translate = i18n.global.t.bind(i18n.global) as (
    key: string,
    ...args: unknown[]
  ) => string;

  function getDefaultNamedValues(
    options: I18nRuntimeOptions,
  ): Record<string, unknown> {
    return getOrganizationTranslationValues(
      options.getOrganizationType?.(),
      translate,
    );
  }

  function isNamedValues(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  // Overload the default translate feature with default named values
  i18n.global.t = (key: string, ...args: unknown[]): string => {
    const defaultNamedValues = getDefaultNamedValues(options);

    if (args.length === 0) {
      return translate(key, defaultNamedValues);
    }

    const [values, ...rest] = args;
    if (isNamedValues(values)) {
      return translate(key, { ...defaultNamedValues, ...values }, ...rest);
    }

    return translate(key, values, ...rest);
  };

  async function ensureLocaleNamespaces(
    locale: Locale,
    namespaces: LocaleNamespace[],
  ): Promise<void> {
    await catalog.ensureLocaleNamespaces(locale, namespaces);

    i18n.global.setLocaleMessage(locale, catalog.getLocaleMessage(locale));

    if (locale !== FALLBACK) {
      i18n.global.setLocaleMessage(
        FALLBACK,
        catalog.getLocaleMessage(FALLBACK),
      );
    }
  }

  async function setLocale(locale: Locale): Promise<void> {
    i18n.global.locale.value = locale;
    setStoredPreferredLanguage(locale);
    await ensureLocaleNamespaces(locale, ["common"]);

    const doc = (
      globalThis as {
        document?: {
          documentElement?: {
            setAttribute(name: string, value: string): void;
          };
        };
      }
    ).document;

    if (doc?.documentElement) {
      doc.documentElement.setAttribute("lang", locale);
      doc.documentElement.setAttribute(
        "data-common-translations-ready",
        "true",
      );
    }
  }

  return { i18n, setLocale, ensureLocaleNamespaces, getDefaultNamedValues };
}
